import React, { useState, useEffect } from 'react';
import { Table, Select, Input, Button, Space, Alert, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import useTranslation from '@hooks/useTranslation';
import useInvokeHubFunction from '@common/hooks/useInvokeHubFunction';
import { 
  HubSecretEntry, 
  GetHubSecretsResponse, 
  AddHubSecretsArgs, 
  AddHubSecretsResponse,
  DeleteHubSecretArgs,
  DeleteHubSecretResponse,
  HubEnvVarInfo 
} from '@src/types/secretMapping_t';
import { KemuHubFunctions } from '@kemu-io/hs-types';
import styles from './HubSecrets.module.css';
import StyledButton from '@components/form-control/styledButton/styledButton';

const { Option } = Select;

/**
 * Component for managing hub secrets
 */
const HubSecrets: React.FC = () => {
  const t = useTranslation('HubSecretsModal');
  const tCommon = t.withBaseKey('CommonWords');
  const invokeHubFunction = useInvokeHubFunction();
  
  const [loading, setLoading] = useState(false);
  const [hubSecrets, setHubSecrets] = useState<Record<string, HubEnvVarInfo[]> | null>(null);
  const [secretEntries, setSecretEntries] = useState<HubSecretEntry[]>([]);
  const [contexts, setContexts] = useState<string[]>([]);
  const [contextSearchText, setContextSearchText] = useState<Record<number, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [originalValues, setOriginalValues] = useState<Record<number, { secretName: string; context: string; value: string }>>({});

  /**
   * Processes hub secrets response and updates local state
   */
  const processHubSecretsResponse = (response: GetHubSecretsResponse | AddHubSecretsResponse | DeleteHubSecretResponse | null) => {
    if (response && Array.isArray(response) && response.length > 0) {
      const secrets = response[0].contexts;
      setHubSecrets(secrets);
      
      // Extract contexts
      const contextNames = Object.keys(secrets);
      setContexts(contextNames);
      
      // Convert to table format
      const entries: HubSecretEntry[] = [];
      Object.entries(secrets).forEach(([context, envVars]) => {
        (envVars as HubEnvVarInfo[]).forEach((envVar: HubEnvVarInfo) => {
          entries.push({
            secretName: envVar.key,
            context,
            value: envVar.encryptedValue, // Show encrypted value for display
            isExisting: true,
            isEditing: false,
          });
        });
      });
      setSecretEntries(entries);
    }
  };

  /**
   * Loads hub secrets data
   */
  const loadData = async () => {
    setLoading(true);
    try {
      const hubData = await invokeHubFunction<GetHubSecretsResponse>(KemuHubFunctions.GetHubSecrets);
      processHubSecretsResponse(hubData);
    } catch (error) {
      console.error('Failed to load hub secrets:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Adds a new secret entry
   */
  const addSecretEntry = () => {
    const newEntry: HubSecretEntry = {
      secretName: '',
      context: '',
      value: '',
      isExisting: false,
      isEditing: true,
    };
    setSecretEntries([...secretEntries, newEntry]);
  };

  /**
   * Removes a secret entry
   */
  const removeSecretEntry = async (index: number) => {
    const entry = secretEntries[index];
    if (entry.isExisting && entry.secretName && entry.context) {
      try {
        setLoading(true);
        setSaveError(null);
        const args: DeleteHubSecretArgs = [{ context: entry.context, secretName: entry.secretName }];
        const response = await invokeHubFunction<DeleteHubSecretResponse>(KemuHubFunctions.DeleteHubSecret, args);
        
        // Update the local state with the response
        processHubSecretsResponse(response);
      } catch (error) {
        console.error('Failed to delete hub secret:', error);
        setSaveError(error instanceof Error ? error.message : t('Error.DeleteFailed'));
      } finally {
        setLoading(false);
      }
    } else {
      // For new entries that haven't been saved yet, just remove from the list
      const newEntries = secretEntries.filter((_, i) => i !== index);
      setSecretEntries(newEntries);
    }
  };

  /**
   * Updates a secret entry
   */
  const updateSecretEntry = (index: number, field: keyof HubSecretEntry, value: string) => {
    const newEntries = [...secretEntries];
    newEntries[index] = {
      ...newEntries[index],
      [field]: value,
    };
    setSecretEntries(newEntries);
  };

  /**
   * Toggles edit mode for an existing secret
   */
  const toggleEditMode = (index: number) => {
    const newEntries = [...secretEntries];
    const entry = newEntries[index];
    
    if (entry.isEditing) {
      // Exiting edit mode - restore original values
      const original = originalValues[index];
      newEntries[index] = {
        ...entry,
        isEditing: false,
        secretName: original?.secretName || entry.secretName,
        context: original?.context || entry.context,
        value: original?.value || entry.value
      };
    } else {
      // Entering edit mode - store original values and clear value for editing
      setOriginalValues(prevOriginal => ({
        ...prevOriginal,
        [index]: {
          secretName: entry.secretName,
          context: entry.context,
          value: entry.value
        }
      }));
      newEntries[index] = {
        ...entry,
        isEditing: true,
        value: '' // Clear value for editing
      };
    }
    
    setSecretEntries(newEntries);
  };

  /**
   * Handles secret name input with uppercase and underscore conversion
   */
  const handleSecretNameChange = (index: number, value: string) => {
    const formattedValue = value.toUpperCase().replace(/\s+/g, '_');
    updateSecretEntry(index, 'secretName', formattedValue);
  };

  /**
   * Handles context change and adds to contexts if new
   */
  const handleContextChange = (index: number, value: string) => {
    updateSecretEntry(index, 'context', value);
    
    // Add to contexts if it's new
    if (value && !contexts.includes(value)) {
      setContexts([...contexts, value]);
    }
    
    // Clear search text when selection is made
    setContextSearchText(prev => ({
      ...prev,
      [index]: ''
    }));
  };

  /**
   * Handles context search text change
   */
  const handleContextSearchChange = (index: number, value: string) => {
    setContextSearchText(prev => ({
      ...prev,
      [index]: value
    }));
  };

  /**
   * Handles context dropdown key press
   */
  const handleContextKeyPress = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const searchText = contextSearchText[index]?.trim();
      if (searchText && !contexts.includes(searchText)) {
        // Add new context and select it
        const newContexts = [...contexts, searchText];
        setContexts(newContexts);
        handleContextChange(index, searchText);
      }
    }
  };

  /**
   * Filters context options based on search text
   */
  const filterContextOptions = (input: string, option: any) => {
    return option?.children?.toLowerCase().includes(input.toLowerCase()) || false;
  };

  /**
   * Checks if there are any changes to save (new entries or edited existing entries)
   */
  const hasChangesToSave = () => {
    return secretEntries.some(entry => 
      !entry.isExisting || entry.isEditing
    );
  };

  /**
   * Checks if all new and edited secret entries have all required fields filled
   */
  const isFormValid = () => {
    return secretEntries.every(entry => 
      !entry.isExisting || entry.isEditing ? (
        entry.secretName.trim() && 
        entry.context.trim() && 
        entry.value.trim()
      ) : true
    );
  };

  /**
   * Handles form submission
   */
  const handleSave = async () => {
    if (!hasChangesToSave() || !isFormValid()) {
      return;
    }

    setLoading(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      // Group secrets by context for the new API
      const secretsToSave: Record<string, { envVarName: string; envVarValue: string }[]> = {};
      
      secretEntries.forEach(entry => {
        // Only save new entries or existing entries that are being edited
        if ((!entry.isExisting || entry.isEditing) && entry.secretName && entry.context && entry.value) {
          if (!secretsToSave[entry.context]) {
            secretsToSave[entry.context] = [];
          }
          secretsToSave[entry.context].push({
            envVarName: entry.secretName,
            envVarValue: entry.value,
          });
        }
      });

      // Only call the API if there are secrets to save
      if (Object.keys(secretsToSave).length > 0) {
        const args: AddHubSecretsArgs = [{ contexts: secretsToSave }];
        const response = await invokeHubFunction<AddHubSecretsResponse>(KemuHubFunctions.AddHubSecrets, args);
        
        // Update the local state with the response
        processHubSecretsResponse(response);
      }
      
      // Show success message
      setSaveSuccess(true);
      // Clear any editing states and reset form data (but preserve the updated secret entries)
      setContextSearchText({});
      setSaveError(null);
      setOriginalValues({});
    } catch (error) {
      console.error('Failed to save hub secrets:', error);
      setSaveError(error instanceof Error ? error.message : t('Error.SaveFailed'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resets all form data to initial state
   */
  const resetFormData = () => {
    setSecretEntries([]);
    setContexts([]);
    setContextSearchText({});
    setSaveError(null);
    setSaveSuccess(false);
    setOriginalValues({});
  };

  /**
   * Table columns configuration
   */
  const columns: ColumnsType<HubSecretEntry & { index: number }> = [
    {
      title: t('Column.SecretName'),
      dataIndex: 'secretName',
      key: 'secretName',
      width: 200,
      render: (secretName: string, record: HubSecretEntry & { index: number }) => {
        if (record.isExisting && !record.isEditing) {
          return <span className={styles.PlainText}>{secretName}</span>;
        }
        return (
          <Input
            autoFocus={true}
            autoCapitalize='on'
            value={secretName}
            onChange={(e) => handleSecretNameChange(record.index, e.target.value)}
            placeholder={t('Placeholder.EnterSecretName')}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: t('Column.Context'),
      dataIndex: 'context',
      key: 'context',
      width: 200,
      fixed: 'left',
      render: (context: string, record: HubSecretEntry & { index: number }) => {
        if (record.isExisting && !record.isEditing) {
          return <span className={styles.PlainText}>{context}</span>;
        }
        return (
          <div className={styles.ContextSelectContainer}>
            <Select
              placeholder={t('Placeholder.SelectContext')}
              value={context || undefined}
              onChange={(value) => handleContextChange(record.index, value)}
              onSearch={(value) => handleContextSearchChange(record.index, value)}
              onKeyDown={(e) => handleContextKeyPress(record.index, e)}
              className={styles.ContextSelect}
              showSearch
              filterOption={filterContextOptions}
              notFoundContent={
                contextSearchText[record.index]?.trim() ? 
                  t('ContextDropdown.PressEnterToAdd') : 
                  t('ContextDropdown.NoContextsFound')
              }
              style={{ width: '100%', maxWidth: '200px' }}
            >
              {contexts.map(contextName => (
                <Option key={contextName} value={contextName}>
                  {contextName}
                </Option>
              ))}
            </Select>
          </div>
        );
      },
    },
    {
      title: t('Column.Value'),
      dataIndex: 'value',
      key: 'value',
      width: 200,
      render: (value: string, record: HubSecretEntry & { index: number }) => {
        if (record.isExisting && !record.isEditing) {
          return <span className={styles.PlainText}>{value || '••••••••'}</span>;
        }
        return (
          <Input
            value={value}
            onChange={(e) => updateSecretEntry(record.index, 'value', e.target.value)}
            placeholder={t('Placeholder.EnterValue')}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: t('Column.Actions'),
      key: 'actions',
      width: 120,
      render: (_, record: HubSecretEntry & { index: number }) => (
        <Space>
          {record.isExisting && (
            <Button
              type="text"
              size="small"
              onClick={() => toggleEditMode(record.index)}
            >
              {record.isEditing ? t('Cancel') : t('Edit')}
            </Button>
          )}
          <Popconfirm
            title={t('DeleteConfirm.Title')}
            description={t('DeleteConfirm.Description')}
            onConfirm={() => removeSecretEntry(record.index)}
            okText={t('DeleteConfirm.Confirm')}
            cancelText={t('DeleteConfirm.Cancel')}
            okType="danger"
          >
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Load data on component mount
  useEffect(() => {
    resetFormData();
    loadData();
  }, []);

  return (
    <div className={styles.Container}>
      {saveError && (
        <Alert
          description={saveError}
          type="error"
          showIcon
          closable
          onClose={() => setSaveError(null)}
          className={styles.ErrorAlert}
        />
      )}
      {saveSuccess && (
        <Alert
          message={t('Success.SecretsSaved')}
          description={t('Success.SecretsSavedDescription')}
          type="success"
          showIcon
          closable
          onClose={() => setSaveSuccess(false)}
          className={styles.SuccessAlert}
        />
      )}
      <p className={styles.Instructions}>{t('Instructions')}</p>
      <div className={styles.Actions}>
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addSecretEntry}
          className={styles.AddButton}
        >
          {t('AddSecret')}
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={secretEntries.map((entry, index) => ({ ...entry, index }))}
        pagination={false}
        rowKey={(record) => `${record.index}-${record.secretName}`}
        size="small"
        className={styles.Table}
        scroll={secretEntries.length > 8 ? { y: 400, x: 600 } : { x: 600 }}
      />
      
      <div className={styles.SaveActions}>
        <StyledButton
          color="primary"
          noShadow
          onClick={handleSave}
          loading={loading}
          disabled={!hasChangesToSave() || !isFormValid()}
          className={styles.SaveButton}
          title={tCommon('Save')}
        />
      </div>
    </div>
  );
};

export default HubSecrets;
